// src/pages/subscription/SubscriptionItemsPage.tsx

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useSubscription, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';

function formatLabel(value: string, prefix: string): string {
  return value.replace(prefix, '').split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '--';
  try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return String(dateStr); }
}

function formatMoney(amount: number | null | undefined): string {
  if (amount == null) return '--';
  return (amount / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export const SubscriptionItemsPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useSubscription();
  const { t } = useI18n();

  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = () => {
    if (!id) return;
    setLoading(true);
    store.ListSubscriptionItems(id).then((res: any) => {
      const list = res?.items?.items ?? res?.items;
      setItems(Array.isArray(list) ? list : []);
    }).catch(() => {
      // Error handled by store
    }).finally(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    fetchItems();
  }, [id]);

  const handleDelete = async (itemId: string) => {
    if (!id || !confirm(t('subscription.deleteItemConfirm'))) return;
    try {
      await store.DeleteSubscriptionItem(id, itemId);
      fetchItems();
    } catch {
      // Error handled by store
    }
  };

  const computeTotal = (): number => {
    return items.reduce((sum: number, item: any) => {
      const quantity = item.quantity ?? 1;
      const unitAmount = item.unit_amount ?? 0;
      return sum + (quantity * unitAmount);
    }, 0);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/subscriptions" className="text-muted-foreground hover:text-foreground">
              {t('subscription.list')}
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <Link to={`/subscriptions/${id}`} className="text-muted-foreground hover:text-foreground">
              {id}
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">{t('subscription.items')}</span>
          </nav>
          <h1 className="text-2xl font-bold text-foreground">{t('subscription.items')}</h1>
        </div>
        <Link to={`/subscriptions/${id}/items/new`}>
          <Button>{t('subscription.addItem')}</Button>
        </Link>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Items Table */}
      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('subscription.itemType')}</TableHead>
              <TableHead>{t('subscription.product')}</TableHead>
              <TableHead>{t('subscription.quantity')}</TableHead>
              <TableHead>{t('subscription.unitAmount')}</TableHead>
              <TableHead>{t('subscription.periodStart')}</TableHead>
              <TableHead>{t('subscription.periodEnd')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableEmpty colSpan={7} message={t('subscription.noItems')} />
            ) : (
              items.map((item: any) => {
                const itemId = item.entity?.id || item.id;
                return (
                  <TableRow key={itemId || Math.random()}>
                    <TableCell>
                      {item.type ? (
                        <Badge variant="primary" size="sm">
                          {formatLabel(item.type, 'SUBSCRIPTION_ITEM_TYPE_')}
                        </Badge>
                      ) : '--'}
                    </TableCell>
                    <TableCell>
                      {String(item.product_name ?? item.product_id ?? '--')}
                    </TableCell>
                    <TableCell>
                      {String(item.quantity ?? 1)}
                    </TableCell>
                    <TableCell>
                      {formatMoney(item.unit_amount)}
                    </TableCell>
                    <TableCell>
                      {formatDate(item.period_start)}
                    </TableCell>
                    <TableCell>
                      {formatDate(item.period_end)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Link to={`/subscriptions/${id}/items/${itemId}/edit`}>
                          <Button variant="ghost" size="sm">{t('common.edit')}</Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={() => handleDelete(itemId)}
                        >
                          {t('common.delete')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Total */}
      {items.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">{t('subscription.total')}</span>
              <span className="text-lg font-bold text-foreground">{formatMoney(computeTotal())}</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
});

export default SubscriptionItemsPage;
